<template>
  <v-col cols="12">
    <v-row no-gutters>
      <v-col v-if="hasFilters" cols="12" class="pb-2">
        <filter-dialog
          ref="filter_dialog"
          v-model="filterDialog"
          :route-name="localRouteName"
          :show-export="showExport"
          :filters.sync="localFilters"
          :schema="schema"
          :with-custom-filters="withCustomFilters"
          :auto-reload="autoReload"
          @export="onExport"
          @without-favorite="onChangeFilter"
          @refresh="onClickRefresh"
          @apply="onChangeFilter"
          @reset="onChangeFilter"
          @remove="onChangeFilter"
        >
          <template #before-filter>
            <slot name="before-filter" />
          </template>
          <template #after-filter>
            <slot name="after-filter" />
          </template>
        </filter-dialog>
      </v-col>
      <v-col cols="12">
        <slot name="under-filter" />
      </v-col>
      <v-col
        cols="12"
        class="sv-datatable"
        :class="{ 'sv-datatable--has-column-select': selectableHeaders }"
      >
        <v-card :class="{ 'sv-card': hasBorder }" flat>
          <v-data-table
            v-model="selected"
            v-resize="onResize"
            class="v-datatable-stripped"
            :fixed-header="fixedHeader"
            :height="height(heightOffset)"
            :headers="filteredHeaders"
            :items="items"
            :options.sync="options"
            :expanded.sync="expanded"
            :server-items-length="count"
            :items-per-page="itemsPerPage"
            :item-class="itemClass"
            :loading="loading"
            :footer-props="{ itemsPerPageOptions: [10, 50, 100] }"
            :multi-sort="multiSort"
            :dense="dense"
            v-bind="dataTableProps"
          >
            <template #header>
              <column-select
                v-if="selectableHeaders"
                v-model="userHeaders"
                :headers="headers"
                :column-select-props="columnSelectProps"
              />
            </template>

            <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
              <slot :name="slot" v-bind="scope" />
            </template>

            <template #no-data>
              <slot name="no-data" />
            </template>

            <template #no-result>
              <slot name="no-result" />
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import FilterDialog from "@/components/Filters/FilterDialog";
import ResizeMixin from "@/mixins/ResizeMixin";
import QueryMixin from "@/mixins/QueryMixin";
import { debounce, isEmpty } from "lodash";
import ColumnSelect from "./ColumnSelect.vue";
import { computed } from "vue";

const DataTableHoc = {
  components: { FilterDialog, ColumnSelect },
  mixins: [QueryMixin, ResizeMixin],
  props: {
    value: {
      type: Array,
      default: () => []
    },
    /** Пропсы таблицы (v-data-table) */

    /**
     * Загрузка таблицы v-data-table (loading)
     */
    loading: {
      type: Boolean,
      default: false
    },
    /** column-select */
    columnSelectProps: {
      type: Object,
      default: () => {
        return { left: false };
      }
    },
    /**
     * Заголовки таблицы v-data-table (headers)
     */
    headers: {
      type: Array,
      default: () => []
    },
    /**
     * Данные/элементы таблицы v-data-table (items)
     */
    items: {
      type: Array,
      default: () => []
    },
    /**
     * Данные/элементы таблицы v-data-table (items)
     */
    itemClass: {
      type: [String, Function],
      default: ""
    },
    /**
     * Опции с пагинацией и сортировкой от таблицы v-data-table (options)
     */
    tableOptions: {
      type: Object,
      default: () => ({})
    },
    /**
     * Состояние expand для элементов таблицы v-data-table (items)
     */
    tableExpanded: {
      type: Array,
      default: () => []
    },
    /**
     * Количество всех записей для v-data-table (server-items-length)
     */
    count: {
      type: [Number, String],
      default: 0
    },
    /**
     * Отступ от высоты всей страницы v-data-table (height)
     */
    heightOffset: {
      type: [Number, String],
      default: 0
    },
    /**
     * Уменьшение высоты строк
     */
    dense: {
      type: Boolean,
      default: false
    },
    /**
     * Кол-во записей на странице v-data-table (itemsPerPage)
     */
    itemsPerPage: {
      type: [Number, String],
      default: 50
    },
    /**
     * Фиксированные заголовки v-data-table (fixedHeader)
     */
    fixedHeader: {
      type: Boolean,
      default: true
    },
    /**
     * Байндинг для всех остальных пропсов v-data-table (v-bind)
     */
    dataTableProps: {
      type: Object,
      default: () => ({})
    },
    /**
     * Автообновление таблицы (в секундах)
     */
    autoReload: {
      type: String,
      default: null
    },
    /** Пропсы фильтров (filter-dialog) */

    /**
     * Объект с фильтрами
     */
    filters: {
      type: Object,
      default: () => ({})
    },
    /**
     * Схема с фильтрами
     */
    schema: {
      type: Object,
      default: () => ({})
    },
    /**
     * Показывать кнопку экспорта
     */
    showExport: {
      type: Boolean,
      default: false
    },
    /**
     * Показывать кнопки для сохранения и выбора пользовательских фильтров
     * Если включен, то вместо фильтров из пропса filters будет отдан приоритет для userFilters если у пользователя есть favorite filter
     */
    withCustomFilters: {
      type: Boolean,
      default: false
    },
    /**
     * Название маршрута откда будут вытаскиваться фильтры
     */
    routeName: {
      type: String,
      default: null
    },

    /**
     * Включает компонент выбора колонок таблицы
     * Колонки отображаются на основе поля selected в headers
     */
    selectableHeaders: {
      type: Boolean,
      default: false
    },
    multiSort: {
      type: Boolean,
      default: false
    },

    /**
     * Есть ли граница обрамления (для случаев когда вкладываем таблицу
     * в более сложные страницы, чем однотабличный список, границу можно убрать)
     */
    hasBorder: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      filterDialog: false,
      options: { ...this.tableOptions },
      initOptions: true,
      iternalHeaders: [],
      userHeaders: [],
      selectedHeaders: []
    };
  },
  computed: {
    hasFilters() {
      return !isEmpty(this.schema);
    },
    localFilters: {
      get() {
        return this.filters;
      },
      set(v) {
        this.$emit("update:filters", v);
      }
    },
    filteredHeaders() {
      const { userHeaders, selectableHeaders, headers } = this;
      if (!selectableHeaders) return [...headers];

      return userHeaders
        .filter(sh => sh.selected)
        .map(sh => headers.find(h => h.value === sh.value));
    },
    localRouteName() {
      return this.routeName || this.$route.name || "public";
    }
  },
  watch: {
    tableOptions: {
      handler() {
        this.options = { ...this.tableOptions };
      }
    },
    options: {
      handler() {
        if (this.initOptions && this.withCustomFilters) {
          this.initOptions = false;
        } else {
          this.fetchData();
        }
      },
      deep: true
    },
    userHeaders(v) {
      this.setStoredUserHeaders(v);
    },

    // fix для случаев когда удалилась единственная запись на последней (не первой) странице
    // В таком случае надо метнуться на предыдущую страницу.
    count(newCount) {
      if (
        this.options.page > 1 &&
        (this.options.page - 1) * this.options.itemsPerPage > newCount - 1
      ) {
        const newPage =
          newCount > 0
            ? Math.floor((newCount - 1) / this.options.itemsPerPage) + 1
            : 1;
        this.options = { ...this.options, page: newPage };
      }
    }
  },
  created() {
    this.initUserHeaders();
  },
  methods: {
    onExport() {
      this.$nextTick(() => {
        const params = this.getQueryOptions();
        this.$emit("export", params);
      });
    },
    onClickRefresh() {
      this.fetchData();
    },
    onChangeFilter() {
      this.options = { ...this.options, page: 1 };
    },
    fetchData: debounce(function() {
      const params = this.getQueryOptions();
      this.$emit("fetch", params);
    }, 500),
    initUserHeaders() {
      this.userHeaders = this.getStoredUserHeaders();
    },
    getStoredUserHeaders() {
      const { localRouteName } = this;
      let userHeaders = localStorage.getItem(`${localRouteName}-table-headers`);

      if (!userHeaders) {
        userHeaders = [];
      } else {
        userHeaders = JSON.parse(userHeaders);
      }

      return this.fixStoredUserHeaders(userHeaders);
    },
    fixStoredUserHeaders(userHeaders) {
      // Пропускаем информацию о столбце, если она не имеет нужных полей
      userHeaders = userHeaders.filter(selectedHeader => {
        return "value" in selectedHeader && "selected" in selectedHeader;
      });

      // Пропускаем информацию и столбце, если такого столбца в таблице вообще не сущесвтует
      userHeaders = userHeaders.filter(uh =>
        this.headers.some(h => uh.value === h.value)
      );

      // Добавляем пропущенные столбцы в информации о столбцах
      const missedHeaders = this.headers.filter(h =>
        userHeaders.every(uh => uh.value !== h.value)
      );

      userHeaders = userHeaders.concat(
        missedHeaders.map(({ value, selected, selectable }) => ({
          value,
          selected: selected ?? true,
          selectable: selectable ?? true
        }))
      );

      return userHeaders;
    },
    setStoredUserHeaders(selected) {
      const { localRouteName } = this;
      localStorage.setItem(
        `${localRouteName}-table-headers`,
        JSON.stringify(selected)
      );
    }
  },
  setup(props, { emit }) {
    const expanded = computed({
      get: () => props.tableExpanded,
      set: val => emit("expanded", val)
    });

    const selected = computed({
      get: () => props.value,
      set: val => emit("input", val)
    });

    return {
      expanded,
      selected
    };
  }
};

export default DataTableHoc;
export { DataTableHoc };
</script>
